import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Users,
  Link as LinkIcon,
  Trash,
  Copy,
  Check,
  Calendar,
  Eye,
  Plus,
} from '@phosphor-icons/react';
import { dashboardService, type AccountUser } from '../../services/widgetService';
import type { Dashboard, DashboardShare, DashboardPublicLink } from '../../types/widget';
import { UserSearchInput } from './UserSearchInput';

interface ShareDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboard: Dashboard;
  currentUserId?: number;
}

export const ShareDashboardModal: React.FC<ShareDashboardModalProps> = ({
  isOpen,
  onClose,
  dashboard,
  currentUserId,
}) => {
  // Internal sharing state
  const [shares, setShares] = useState<DashboardShare[]>([]);
  const [sharesLoading, setSharingLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [addingUser, setAddingUser] = useState(false);

  // Public links state
  const [publicLinks, setPublicLinks] = useState<DashboardPublicLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  // Load shares and public links when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setSharingLoading(true);
      setLinksLoading(true);

      try {
        const [sharesData, linksData] = await Promise.all([
          dashboardService.getDashboardShares(dashboard.id),
          dashboardService.getPublicLinks(dashboard.id),
        ]);
        setShares(sharesData);
        setPublicLinks(linksData);
      } catch (err) {
        console.error('Failed to load sharing data:', err);
        setShareError('Failed to load sharing data');
      } finally {
        setSharingLoading(false);
        setLinksLoading(false);
      }
    };

    loadData();
  }, [isOpen, dashboard.id]);

  // Handle adding a user share
  const handleAddShare = useCallback(
    async (user: AccountUser) => {
      setAddingUser(true);
      setShareError(null);

      try {
        // Use user_uuid (snowflake ID) for the API request
        const newShare = await dashboardService.shareDashboard(dashboard.id, user.user_uuid);
        // Merge user info from AccountUser since API response may not include it
        setShares((prev) => [...prev, {
          ...newShare,
          user_first_name: newShare.user_first_name ?? user.first_name,
          user_last_name: newShare.user_last_name ?? user.last_name,
          user_email: newShare.user_email ?? user.email,
        }]);
      } catch (err: any) {
        const message = err?.response?.data?.message || 'Failed to share dashboard';
        setShareError(message);
      } finally {
        setAddingUser(false);
      }
    },
    [dashboard.id]
  );

  // Handle revoking a share
  const handleRevokeShare = useCallback(
    async (userUuid: string) => {
      try {
        // Use user_uuid (snowflake ID) for the API request
        await dashboardService.revokeDashboardShare(dashboard.id, userUuid);
        setShares((prev) => prev.filter((s) => s.shared_with_user_id !== userUuid));
      } catch (err) {
        console.error('Failed to revoke share:', err);
        setShareError('Failed to revoke access');
      }
    },
    [dashboard.id]
  );

  // Handle creating a public link
  const handleCreatePublicLink = useCallback(async () => {
    setCreatingLink(true);

    try {
      const newLink = await dashboardService.createPublicLink(dashboard.id);
      setPublicLinks((prev) => [...prev, newLink]);
    } catch (err) {
      console.error('Failed to create public link:', err);
    } finally {
      setCreatingLink(false);
    }
  }, [dashboard.id]);

  // Handle deactivating a public link
  const handleDeactivateLink = useCallback(
    async (linkId: string) => {
      try {
        await dashboardService.deactivatePublicLink(dashboard.id, linkId);
        setPublicLinks((prev) => prev.filter((l) => l.id !== linkId));
      } catch (err) {
        console.error('Failed to deactivate link:', err);
      }
    },
    [dashboard.id]
  );

  // Handle copying link to clipboard
  const handleCopyLink = useCallback((link: DashboardPublicLink) => {
    // Construct frontend URL instead of using backend API URL
    const frontendUrl = `${window.location.origin}/shared/${link.token}`;
    navigator.clipboard.writeText(frontendUrl);
    setCopiedLinkId(link.id);
    setTimeout(() => setCopiedLinkId(null), 2000);
  }, []);

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get list of already shared user UUIDs (for exclusion in user search)
  const sharedUserUuids = shares.map((s) => s.shared_with_user_id);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Share Dashboard
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Internal Sharing Section */}
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <Users size={18} className="text-gray-500" />
              <h3 className="text-sm font-medium text-gray-700">
                Share with Team Members
              </h3>
            </div>

            {/* User Search */}
            <UserSearchInput
              excludeUserUuids={sharedUserUuids}
              currentUserId={currentUserId}
              onSelect={handleAddShare}
              disabled={addingUser}
              placeholder="Search for a team member..."
            />

            {/* Error message */}
            {shareError && (
              <p className="mt-2 text-sm text-red-500">{shareError}</p>
            )}

            {/* Shared Users List */}
            <div className="mt-4">
              {sharesLoading ? (
                <p className="text-sm text-gray-500">Loading shares...</p>
              ) : shares.length === 0 ? (
                <p className="text-sm text-gray-400">
                  Not shared with anyone yet
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 mb-2">
                    Shared with {shares.length} {shares.length === 1 ? 'person' : 'people'}
                  </p>
                  {shares.map((share) => (
                    <div
                      key={share.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-xs font-medium text-blue-700">
                            {share.user_first_name[0]}
                            {share.user_last_name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {share.user_first_name} {share.user_last_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {share.user_email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Eye size={12} /> View only
                        </span>
                        <button
                          onClick={() => handleRevokeShare(share.shared_with_user_id)}
                          className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove access"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Public Links Section */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <LinkIcon size={18} className="text-gray-500" />
                <h3 className="text-sm font-medium text-gray-700">
                  Public Link Sharing
                </h3>
              </div>
              <button
                onClick={handleCreatePublicLink}
                disabled={creatingLink}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Plus size={14} />
                {creatingLink ? 'Creating...' : 'Create Link'}
              </button>
            </div>

            {/* Public Links List */}
            {linksLoading ? (
              <p className="text-sm text-gray-500">Loading links...</p>
            ) : publicLinks.length === 0 ? (
              <p className="text-sm text-gray-400">
                No public links created yet
              </p>
            ) : (
              <div className="space-y-3">
                {publicLinks.map((link) => (
                  <div
                    key={link.id}
                    className="p-3 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono text-gray-600 truncate">
                          {`${window.location.origin}/shared/${link.token}`}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                          {link.shared_email && (
                            <span>Sent to: {link.shared_email}</span>
                          )}
                          {link.expires_at && (
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              Expires: {formatDate(link.expires_at)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Eye size={12} />
                            {link.access_count} views
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleCopyLink(link)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700 transition-colors"
                          title="Copy link"
                        >
                          {copiedLinkId === link.id ? (
                            <Check size={16} className="text-green-500" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeactivateLink(link.id)}
                          className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition-colors"
                          title="Revoke link"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="mt-4 text-xs text-gray-400">
              Anyone with a public link can view this dashboard without signing in.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareDashboardModal;
